import { create } from 'zustand';
import type { CustomerStatus } from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';

export type CustomersSort = 'recent' | 'name';
export type CustomerEditorTab = 'general' | 'projects' | 'contracts' | 'invoices';

interface CustomerSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
  bulkDraft: Record<string, never>;
}

function createInitialSelectionSession(): CustomerSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0,
    bulkDraft: {}
  };
}

interface CustomersViewStore {
  statusFilter: 'all' | CustomerStatus;
  categoryFilter: 'all' | 'unassigned' | string;
  sort: CustomersSort;
  editorTab: CustomerEditorTab;
  selectionSession: CustomerSelectionSession;
  setStatusFilter: (value: 'all' | CustomerStatus) => void;
  setCategoryFilter: (value: 'all' | 'unassigned' | string) => void;
  setSort: (value: CustomersSort) => void;
  setEditorTab: (value: CustomerEditorTab) => void;
  setViewMode: (value: 'grid' | 'list') => Promise<void>;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
}

export const useCustomersViewStore = create<CustomersViewStore>((set) => ({
  statusFilter: 'all',
  categoryFilter: 'all',
  sort: 'recent',
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCategoryFilter: (categoryFilter) => set({ categoryFilter }),
  setSort: (sort) => set({ sort }),
  setEditorTab: (editorTab) => set({ editorTab }),
  setViewMode: async (customersViewMode) => {
    await useShellStore.getState().savePreferences({ customersViewMode });
  },
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    }))
}));
